/**
 * <pdp-gallery-nav> — makes PREV / NEXT work in the overlay gallery view.
 *
 * Why this is needed: in the `thumbnail_slider` desktop layout,
 * section-main-product.css hides every media item except the active one
 * (`.product--thumbnail_slider ... .product__media-item:not(.is-active)
 * { display: none }`). SliderComponent.initPages() then filters items by
 * `clientWidth > 0`, finds only one, and bails before setting
 * `sliderItemOffset`. Its onButtonClick therefore computes
 * `scrollLeft + (step * undefined)` = NaN and the buttons do nothing.
 *
 * Rather than re-implement a slider, this drives the gallery the same way the
 * thumbnails already do: it clicks the adjacent thumbnail, which runs
 * media-gallery.js `setActiveMedia` and keeps every active state correct.
 *
 * Markup contract: wraps the existing .slider-buttons block and carries
 * data-section so it can find the thumbnail rail.
 */
(function () {
  class PdpGalleryNav extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.currentElement = this.querySelector('.slider-counter--current');

      if (!this.prevButton || !this.nextButton) return;

      // Registered here, before SliderComponent.init() runs (it is deferred by
      // an IntersectionObserver), so this handler goes first and can stop the
      // broken scroll handler from also firing.
      this.prevButton.addEventListener('click', this.onClick);
      this.nextButton.addEventListener('click', this.onClick);

      // The active slide also changes when a thumbnail is clicked directly or
      // when a variant switch calls setActiveMedia(). Watching the rail for the
      // `is-active` class covers every path, so the counter and the rail scroll
      // stay correct no matter what moved the gallery.
      this.observeRail();
      this.sync();
    }

    observeRail() {
      const rail = document.getElementById(`GalleryThumbnails-${this.sectionId}`);
      if (!rail) return;

      this.observer = new MutationObserver(() => {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.sync(), 50);
      });
      this.observer.observe(rail, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class', 'aria-current'],
      });
    }

    sync() {
      const index = this.activeIndex;
      this.syncCounter(index);
      this.scrollActiveIntoView(index);
    }

    scrollActiveIntoView(index) {
      const item = this.thumbnails[index];
      if (!item) return;

      const rail = item.parentElement;
      if (!rail || rail.scrollHeight <= rail.clientHeight) return;

      const top = item.offsetTop - rail.offsetTop;
      const bottom = top + item.offsetHeight;

      if (top < rail.scrollTop) {
        rail.scrollTop = top;
      } else if (bottom > rail.scrollTop + rail.clientHeight) {
        rail.scrollTop = bottom - rail.clientHeight;
      }
    }

    disconnectedCallback() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onClick);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.syncTimer);
    }

    get thumbnails() {
      const rail = document.getElementById(`GalleryThumbnails-${this.sectionId}`);
      if (!rail) return [];
      return Array.from(rail.querySelectorAll('.thumbnail-list__item')).filter((item) => item.offsetParent !== null);
    }

    get activeIndex() {
      // media-gallery.js setActiveThumbnail() puts `is-active` on the <li>
      // itself and aria-current on its inner button, so read the item.
      const index = this.thumbnails.findIndex(
        (item) => item.classList.contains('is-active') || item.querySelector('[aria-current="true"]'),
      );
      return index === -1 ? 0 : index;
    }

    onClick(event) {
      const thumbs = this.thumbnails;
      if (thumbs.length < 2) return;

      // SliderComponent binds its own handler to these buttons; its scroll math
      // is unusable in this layout, so stop it running on top of ours.
      event.preventDefault();
      event.stopImmediatePropagation();

      const step = event.currentTarget.name === 'next' ? 1 : -1;
      const next = (this.activeIndex + step + thumbs.length) % thumbs.length;

      const trigger = thumbs[next].querySelector('button') || thumbs[next];
      trigger.click();
    }

    syncCounter(index) {
      if (!this.currentElement) return;
      const position = typeof index === 'number' ? index : this.activeIndex;
      this.currentElement.textContent = position + 1;
    }
  }

  if (!customElements.get('pdp-gallery-nav')) {
    customElements.define('pdp-gallery-nav', PdpGalleryNav);
  }
})();
